import {
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject,
} from '@angular/core';

const ELLIPSIS = '...';

/**
 * Author name: Yaron Hakon
 * Date: 14/06/2026
 * Class Purpose: Truncates overflowing single-line text with an ellipsis using ResizeObserver.
 * FR/Task ID: 2415
 * Pre-condition: Host element sits inside a width-bounded flex/grid container.
 * Post-condition: Host textContent ends with "..." when content exceeds available width.
 * ToDo: None.
 */
@Directive({
  selector: '[appTruncateOverflowText]',
  standalone: true,
})
export class TruncateOverflowTextDirective implements OnChanges, OnDestroy {
  /** Full label text to render; truncated when the host width is too narrow. */
  @Input('appTruncateOverflowText') text = '';

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly resizeObserver =
    typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => this.applyTruncation())
      : undefined;

  private fullText = '';

  constructor() {
    const host = this.elementRef.nativeElement;
    host.style.overflow = 'hidden';
    host.style.whiteSpace = 'nowrap';
    host.style.display = 'block';
    this.resizeObserver?.observe(host);

    const parent = host.parentElement;
    if (parent) {
      this.resizeObserver?.observe(parent);
    }
  }

  /**
   * Author name: yaron hakon
   * Date: 14/06/2026
   * Method Purpose: Re-applies truncation when the bound text changes.
   * FR/Task ID: 2415
   * Pre-condition: None.
   * Post-condition: Visible host text reflects the latest input value.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['text']) {
      this.fullText = this.text ?? '';
      this.applyTruncation();
    }
  }

  /**
   * Author name: yaron hakon
   * Date: 14/06/2026
   * Method Purpose: Disconnects ResizeObserver on destroy.
   * FR/Task ID: 2415
   * Pre-condition: None.
   * Post-condition: Observer is disconnected.
   */
  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  private applyTruncation(allowRetry = true): void {
    const host = this.elementRef.nativeElement;
    const text = this.fullText;

    if (!text) {
      host.textContent = '';
      return;
    }

    const maxWidth = this.resolveMaxWidth();
    if (maxWidth <= 0 && allowRetry) {
      requestAnimationFrame(() => this.applyTruncation(false));
      return;
    }

    if (maxWidth <= 0) {
      host.textContent = text;
      return;
    }

    host.style.maxWidth = `${maxWidth}px`;
    host.style.width = `${maxWidth}px`;
    host.textContent = text;

    if (host.scrollWidth <= maxWidth) {
      return;
    }

    let low = 0;
    let high = text.length;

    while (low < high) {
      const mid = Math.ceil((low + high) / 2);
      host.textContent = `${text.slice(0, mid)}${ELLIPSIS}`;

      if (host.scrollWidth > maxWidth) {
        high = mid - 1;
      } else {
        low = mid;
      }
    }

    host.textContent =
      low > 0 ? `${text.slice(0, low)}${ELLIPSIS}` : ELLIPSIS;
  }

  private resolveMaxWidth(): number {
    const host = this.elementRef.nativeElement;
    const wrap = host.parentElement;
    if (!wrap) {
      return host.clientWidth;
    }

    const wrapWidth = wrap.clientWidth;
    if (wrapWidth <= 0) {
      return 0;
    }

    const wrapStyle = getComputedStyle(wrap);
    const gap =
      parseFloat(wrapStyle.columnGap || wrapStyle.gap || '0') || 0;

    let siblingsWidth = 0;
    let siblingCount = 0;
    for (const child of Array.from(wrap.children)) {
      if (child !== host) {
        siblingsWidth += (child as HTMLElement).offsetWidth;
        siblingCount += 1;
      }
    }

    return Math.max(0, wrapWidth - siblingsWidth - gap * siblingCount);
  }
}
